import React, { useState, useEffect, useMemo } from "react";
import AppLayout from "./Components/AppLayout";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReactGA from "react-ga";
import mixpanel from "mixpanel-browser";
import { Provider } from "react-redux";
import { store } from "./Store";
import { onAuthStateChanged, signInWithCustomToken } from "firebase/auth";
import { auth } from "../src/Components/firebase/firebase";
import { AuthProvider } from "./AuthContext";
import { BrowserRouter, Switch } from "react-router-dom";
import PrivateRoute from "../src/PrivateRoute";
import Loader from "./Components/Loader";
import { Urls } from "./Urls";
import { UserDataProvider } from "./UserContext";
import { UserDetailsProvider } from "./UserDetailsContext";
import { EditbleEmailsProvider } from "./EditbleEmailsContext";
import Axios from "axios";

// Import CSS files for styling various components
import "./Components/AdminPage/Dashboard/liveDashboard.css";
import "./Components/AdminPage/Dashboard/OverallDashboard.css";
import "./Components/AdvancedAuction/AdvancedAuction.css";
import "./Components/AdvancedAuction/AuctionDashboard/LiveDashboard.css";
import "./Components/AdvancedAuction/AuctionDashboard/OverallSummary.css";
import "./Components/AnalysisNow/AnalysisNow.css";
import "./Components/Auction/Auction.css";
import "./Components/Auction/ModalBox/modal.css";
import "./Components/AuctionShortlist/AuctionShortlist.css";
import "./Components/Body/body.css";
import "./Components/CompanySelect/Company.css";
import "./Components/Documents/Documents.css";
import "./Components/Lanes/Lane.css";
import "./Components/Lanes/Filters/filters.css";
import "./Components/Loader/Loader.css";
import "./Components/ModalBox/modal.css";
import "./Components/Notifications/Notifications.css";
import "./Components/Overlay/Overlay.css";
import "./Components/Schedule/Schedule.css";
import "./Components/ToolTip/modalBox.css";
import "./Components/TransporterClientScreen/TransporterDetailsBody/Contact/ContactCard.css";
import "./Content/css/react-datepicker.css";

import { CompanyDataProvider } from "./CompanyProfileContext";
import { CompanyDetailsProvider } from "./CompanyDetailsContext";
import { MyCompanyProfileDetailsProvider } from "./MyCompanyProfileContext";
import { SidebarProvider } from "./SidebarContext";
import { tpmpAuth } from "./Components/firebase/firebaseRealtime";
import axios from "./Axios";

// Initialize Google Analytics and Mixpanel with respective IDs
ReactGA.initialize(Urls.gaId);
mixpanel.init(Urls.mixpanelId);

const App = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tpmpTokenAcquired, setTpmpTokenAcquired] = useState(false);
  const [clientIP, setClientIP] = useState(null);

  const isValidFirebaseCustomToken = (token) => {
    if (!token || typeof token !== "string") return false;
    const parts = token.split(".");
    return parts.length === 3 && parts.every((p) => p.length > 0);
  };

  useEffect(() => {
    setLoading(true);
    // Extract the handoff idToken from the URL fragment (never sent to server/logs)
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const idToken = hashParams.get("h");
    if (idToken) {
      // Strip it from the address bar immediately so it doesn't linger in history
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search
      );
    }

    const queryParams = new URLSearchParams(window.location.search);
    
    // const id = queryParams.get("id");

    const auction = queryParams.get("auction");
    const screen = queryParams.get("screen"); // e.g., 'analyzeNow', 'documents'
    const procurementId = queryParams.get("procurementId");

    // Check authentication state changes
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (idToken) {
        // Exchange the parent-app idToken for a Firebase custom token and sign in
        Axios.get(Urls.firebase_token, {
          headers: { Authorization: `Bearer ${idToken}` },
          // params : {uid : id}
        })
          .then((res) => {
            signInWithCustomToken(auth, res.data.token)
              .then((userCredential) => {
                setCurrentUser(userCredential.user);
                setLoading(false);

                // Redirect to '/auction' if 'auction' parameter is 'true'
                if (auction === "true" || auction === "auction") {
                  window.location.href = "/auction";
                } else if (screen === "analyzeNow" && procurementId) {
                  window.location.href = `/analyzeNow/${procurementId}`;
                }
              })
              .catch((error) => {
                console.log(error);
                setLoading(false);
              });
          })
          .catch((err) => {
            console.log(err);
            setLoading(false);
          });
      } else if (user) {
        // If user is authenticated, set current user
        setCurrentUser(user);
        setLoading(false);
      } else {
        // Redirect to client-side login if no idToken and user is not authenticated
        window.location.href = `${Urls.clientSideLogin}`;
        setLoading(false);
      }
    });

    // Fetch and store client's IP address
    Axios.get("https://api.ipify.org?format=json")
      .then((res) => setClientIP(res.data.ip))
      .catch((err) => console.log(err));

    return () => unsubscribe();
  }, []);

  //creating new token for client so that it can access TPMP auction data in real time
  useEffect(() => {
    if (!currentUser || tpmpTokenAcquired) return;
    const loginToTPMP = async () => {
      try {
        const resp = await axios.post(`${Urls.auctionlorri1}/customToken`);
        const newCustomToken = resp?.data?.token;
        if (!isValidFirebaseCustomToken(newCustomToken)) {
          // console.error("Invalid TPMP custom token");
          toast.error("Invalid TPMP custom token");
          return;
        }

        await signInWithCustomToken(tpmpAuth, newCustomToken);
        // console.log("TPMP signed in successfully");
        setTpmpTokenAcquired(true);
      } catch (err) {
        console.error("Error logging into TPMP:", err);
      }
    };
    loginToTPMP();
  }, [currentUser, tpmpTokenAcquired]);

  const authValue = useMemo(
    () => ({ currentUser, clientIP }),
    [currentUser, clientIP]
  );

  return (
    <>
      <ToastContainer />
      {loading ? (
        <Loader show={loading} />
      ) : (
        <SidebarProvider>
          <BrowserRouter>
            <AuthProvider value={authValue}>
              {!currentUser ? (
                <Switch>
                  {/* Add routes for authentication here if needed */}
                </Switch>
              ) : (
                <PrivateRoute>
                  <UserDetailsProvider>
                    <UserDataProvider>
                      <CompanyDetailsProvider>
                        <MyCompanyProfileDetailsProvider>
                          <EditbleEmailsProvider>
                            <CompanyDataProvider>
                              <Provider store={store}>
                                <AppLayout />
                              </Provider>
                            </CompanyDataProvider>
                          </EditbleEmailsProvider>
                        </MyCompanyProfileDetailsProvider>
                      </CompanyDetailsProvider>
                    </UserDataProvider>
                  </UserDetailsProvider>
                </PrivateRoute>
              )}
            </AuthProvider>
          </BrowserRouter>
        </SidebarProvider>
      )}
    </>
  );
};

export default App;
